import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "../../contexts/LanguageContext";
import { api } from "../../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip as RTooltip, PieChart, Pie, Cell, Legend } from "recharts";
import { ArrowRight, Inbox, FilePen, GraduationCap, CreditCard, TrendingUp } from "lucide-react";

const ELEM_GRADES = ["gs", "cp", "ce1", "ce2", "cm1", "cm2"];

function annualFor(grade) {
  // Flat tuition across all grades: 1995 € / course / year
  return 1995;
}

function fmt(n) { return Math.round(n).toLocaleString("fr-FR"); }

export default function AdminHome() {
  const { t, lang } = useLang();
  const [overview, setOverview] = useState(null);
  const [inquiries, setInquiries] = useState([]);
  const [applications, setApplications] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [payments, setPayments] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/analytics/overview"),
      api.get("/inquiries"),
      api.get("/applications"),
      api.get("/enrollments"),
      api.get("/payments"),
    ]).then(([a, b, c, d, e]) => {
      setOverview(a.data); setInquiries(b.data); setApplications(c.data); setEnrollments(d.data); setPayments(e.data);
    }).catch(() => {});
  }, []);

  if (!overview) return <div>{t("common.loading")}</div>;

  const started = applications.filter(a => a.status === "started");
  const received = applications.filter(a => ["submitted", "under_review"].includes(a.status));
  const accepted = applications.filter(a => a.status === "accepted");
  const enrolledApps = applications.filter(a => a.status === "enrolled");

  // Revenue forecast: sum of annualFor(grade) for every enrolled or accepted student
  const forecastFromEnrolled = enrollments.reduce((sum, e) => sum + annualFor(e.student?.grade), 0);
  const forecastFromAccepted = accepted.reduce((sum, a) => sum + annualFor(a.student?.grade), 0);
  const collected = payments.filter(p => ["deposit_paid", "paid_in_full", "plan_active"].includes(p.status)).reduce((s, p) => s + (p.amount || 0), 0);

  const cards = [
    { label: t("admin.totalInquiries"), value: overview.total_inquiries, key: "inquiries", icon: Inbox, to: "/admin/inquiries" },
    { label: lang === "fr" ? "Candidatures commencées" : "Applications started", value: started.length, key: "started", icon: FilePen, to: "/admin/applications" },
    { label: lang === "fr" ? "Candidatures reçues" : "Applications received", value: received.length, key: "received", icon: FilePen, to: "/admin/admissions" },
    { label: t("admin.acceptedStudents"), value: accepted.length, key: "accepted", icon: GraduationCap, to: "/admin/applications" },
    { label: t("admin.enrolledStudents"), value: enrollments.length, key: "enrolled", icon: GraduationCap, to: "/admin/enrollments" },
    { label: lang === "fr" ? "Revenu prévu" : "Forecast revenue", value: `${fmt(forecastFromEnrolled + forecastFromAccepted)} €`, key: "forecast", icon: TrendingUp, to: "/admin/payments" },
    { label: lang === "fr" ? "Encaissé" : "Collected", value: `${fmt(collected)} €`, key: "collected", icon: CreditCard, to: "/admin/payments" },
    { label: t("admin.conversionRate"), value: overview.conversion_rate + "%", key: "conversion", icon: TrendingUp, to: "/admin/analytics" },
  ];

  const COLORS = ["#2D78F6", "#EA5658", "#F8CD46", "#66CA9D", "#926EF7", "#14274B"];

  return (
    <div data-testid="admin-home" className="space-y-6">
      <div>
        <p className="overline mb-1">OFA France · Admin</p>
        <h1 className="font-display font-bold text-3xl tracking-tight">{lang === "fr" ? "Tableau de bord — Funnel d'inscription" : "Dashboard — Enrollment funnel"}</h1>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {cards.map(c => (
          <Link key={c.key} to={c.to}>
            <Card className="hover:border-primary/50 transition-colors h-full" data-testid={`metric-${c.key}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <c.icon className="w-4 h-4 text-primary" strokeWidth={1.75} />
                  <ArrowRight className="w-3 h-3 text-muted-foreground" />
                </div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mt-2">{c.label}</div>
                <div className="font-display font-bold text-2xl mt-1 tabular-nums">{c.value}</div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* Two-column live lists */}
      <div className="grid lg:grid-cols-2 gap-4">
        {/* Recent inquiries */}
        <Card data-testid="section-recent-inquiries">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base font-display tracking-tight">{lang === "fr" ? "Demandes d'informations récentes" : "Recent inquiries"}</CardTitle>
            <Link to="/admin/inquiries"><Button size="sm" variant="ghost">{lang === "fr" ? "Tout voir" : "View all"} →</Button></Link>
          </CardHeader>
          <CardContent className="p-0">
            <Listing
              items={inquiries.slice(0, 6)}
              empty={lang === "fr" ? "Aucune demande" : "No inquiries"}
              row={(i) => ({
                title: `${i.parent_first_name} ${i.parent_last_name}`,
                sub: `${i.child_first_name} ${i.child_last_name} · ${i.current_grade || "—"} · ${i.country || "—"}`,
                badge: { label: t(`statuses.${i.status}`), variant: i.status === "new" ? "default" : "outline" },
                meta: new Date(i.created_at).toLocaleDateString(),
              })}
            />
          </CardContent>
        </Card>

        {/* Started applications */}
        <Card data-testid="section-started-apps">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base font-display tracking-tight">{lang === "fr" ? "Candidatures en cours" : "In-progress applications"}</CardTitle>
            <Link to="/admin/applications"><Button size="sm" variant="ghost">{lang === "fr" ? "Tout voir" : "View all"} →</Button></Link>
          </CardHeader>
          <CardContent className="p-0">
            <Listing
              items={started.slice(0, 6)}
              empty={lang === "fr" ? "Aucune candidature débutée" : "No applications started"}
              row={(a) => ({
                title: `${a.student?.first_name || ""} ${a.student?.last_name || ""}`,
                sub: `${a.family?.parent_first_name || ""} ${a.family?.parent_last_name || ""} · ${a.program_choice ? t(`programs.${a.program_choice}`) : "—"}`,
                badge: { label: t(`statuses.${a.status}`), variant: "outline" },
                meta: a.student?.grade || "—",
              })}
            />
          </CardContent>
        </Card>

        {/* Received applications */}
        <Card data-testid="section-received-apps">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base font-display tracking-tight">{lang === "fr" ? "Candidatures reçues à examiner" : "Applications to review"}</CardTitle>
            <Link to="/admin/admissions"><Button size="sm" variant="ghost">{lang === "fr" ? "Pipeline" : "Pipeline"} →</Button></Link>
          </CardHeader>
          <CardContent className="p-0">
            <Listing
              items={received.slice(0, 6)}
              empty={lang === "fr" ? "Aucune candidature à examiner" : "No applications to review"}
              row={(a) => ({
                title: `${a.student?.first_name} ${a.student?.last_name}`,
                sub: `${a.family?.email || ""} · ${a.student?.grade || "—"}`,
                badge: { label: t(`statuses.${a.status}`), variant: a.status === "under_review" ? "default" : "outline" },
                meta: a.submitted_at ? new Date(a.submitted_at).toLocaleDateString() : "—",
              })}
            />
          </CardContent>
        </Card>

        {/* Accepted students */}
        <Card data-testid="section-accepted">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base font-display tracking-tight">{lang === "fr" ? "Élèves acceptés (en attente d'inscription)" : "Accepted students (pending enrollment)"}</CardTitle>
            <Link to="/admin/applications"><Button size="sm" variant="ghost">→</Button></Link>
          </CardHeader>
          <CardContent className="p-0">
            <Listing
              items={accepted.slice(0, 6)}
              empty={lang === "fr" ? "Aucun élève accepté" : "No accepted students"}
              row={(a) => ({
                title: `${a.student?.first_name} ${a.student?.last_name}`,
                sub: `${lang === "fr" ? "Classe" : "Grade"} : ${a.student?.grade || "—"} · ${a.program_choice ? t(`programs.${a.program_choice}`) : "—"}`,
                badge: { label: t("statuses.accepted"), variant: "default" },
                meta: `${annualFor(a.student?.grade).toLocaleString("fr-FR")} €`,
              })}
            />
          </CardContent>
        </Card>
      </div>

      {/* Enrolled students table */}
      <Card data-testid="section-enrolled">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base font-display tracking-tight">{lang === "fr" ? "Élèves inscrits" : "Enrolled students"}</CardTitle>
          <Link to="/admin/enrollments"><Button size="sm" variant="ghost">→</Button></Link>
        </CardHeader>
        <CardContent className="p-0">
          {enrollments.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">{lang === "fr" ? "Aucun élève inscrit pour le moment" : "No enrolled students yet"}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-secondary/50 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                  <tr>
                    <th className="text-left p-3">{lang === "fr" ? "Élève" : "Student"}</th>
                    <th className="text-left p-3">{lang === "fr" ? "Classe" : "Grade"}</th>
                    <th className="text-left p-3">Track</th>
                    <th className="text-left p-3">{lang === "fr" ? "Famille" : "Family"}</th>
                    <th className="text-left p-3">{lang === "fr" ? "Plan de paiement" : "Payment plan"}</th>
                    <th className="text-right p-3 tabular-nums">{lang === "fr" ? "Tarif annuel" : "Annual"}</th>
                  </tr>
                </thead>
                <tbody>
                  {enrollments.map(e => {
                    const app = applications.find(a => a.id === e.application_id);
                    const track = app?.family_info?.track || app?.program_choice || "—";
                    return (
                      <tr key={e.id} data-testid={`enrolled-${e.id}`} className="border-t border-border">
                        <td className="p-3 font-medium">{e.student?.first_name} {e.student?.last_name}</td>
                        <td className="p-3"><Badge variant="outline">{e.student?.grade || "—"}</Badge></td>
                        <td className="p-3"><Badge>{track === "fluent" ? "Fluent Track" : track === "bridge" ? "Bridge Track" : track === "diploma" ? "Diploma" : track === "fluent_track" ? "Fluent Track" : track === "bridge_track" ? "Bridge Track" : track}</Badge></td>
                        <td className="p-3 text-xs">{e.family?.parent_first_name} {e.family?.parent_last_name} <div className="text-muted-foreground">{e.family?.email}</div></td>
                        <td className="p-3 text-xs">{t(`payments.${e.payment_mode}`)}</td>
                        <td className="p-3 text-right tabular-nums font-semibold">{annualFor(e.student?.grade).toLocaleString("fr-FR")} €</td>
                      </tr>
                    );
                  })}
                  <tr className="border-t-2 border-primary bg-primary/5 font-semibold">
                    <td className="p-3" colSpan="5">{lang === "fr" ? "Total revenu inscrits" : "Enrolled revenue total"}</td>
                    <td className="p-3 text-right tabular-nums">{fmt(forecastFromEnrolled)} €</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Distribution charts */}
      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <CardHeader><CardTitle className="text-base">{t("admin.byProgram")}</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={overview.by_program}>
                <XAxis dataKey="name" tick={{ fontSize: 10 }} tickFormatter={(v) => v.replace(/_/g, " ").slice(0, 20)} />
                <YAxis tick={{ fontSize: 10 }} />
                <RTooltip />
                <Bar dataKey="value" fill="#2D78F6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base">{t("admin.byCountry")}</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={overview.by_country} dataKey="value" nameKey="name" outerRadius={75} label>
                  {overview.by_country.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Legend wrapperStyle={{ fontSize: 10 }} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Listing({ items, row, empty }) {
  if (items.length === 0) return <div className="p-8 text-center text-sm text-muted-foreground">{empty}</div>;
  return (
    <ul className="divide-y divide-border">
      {items.map((it, i) => {
        const r = row(it);
        return (
          <li key={it.id || i} className="p-3 px-4 hover:bg-secondary/30 transition-colors flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm truncate">{r.title}</div>
              <div className="text-xs text-muted-foreground truncate">{r.sub}</div>
            </div>
            <Badge variant={r.badge.variant} className="shrink-0">{r.badge.label}</Badge>
            <span className="text-xs text-muted-foreground tabular-nums shrink-0 w-20 text-right">{r.meta}</span>
          </li>
        );
      })}
    </ul>
  );
}
